import { Fragment, useEffect, useMemo, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';
import { Tabs, TabsList, Tab, TabPanel } from '@/components/tabs';
import * as Yup from 'yup';
import {
  useGetBookingCancellationPolicyQuery,
  useUpdateBookingCancellationPolicyMutation,
  useGetBookingNoShowPolicyQuery,
  useUpdateBookingNoShowPolicyMutation,
} from '@/services/SystemConfigService';
import { PenaltyType } from '@/types/penalty-config.types';
import NumberInput from '@/components/inputControls/NumberInput';


type FormState = {
  free_cancellation_hours: number | '';
  penalty_type: PenaltyType | string;
  penalty_value: number | '';
  min_penalty_amount_eur?: number | '';
  max_penalty_amount_eur?: number | '';
};

const PenaltyConfigManagement = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState<'cancellation' | 'no_show'>(
    'cancellation'
  );

  const [formData, setFormData] = useState<FormState>({
    free_cancellation_hours: 0,
    penalty_type: 'FIXED',
    penalty_value: '',
    min_penalty_amount_eur: '',
    max_penalty_amount_eur: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: cancellationPolicy, isLoading: isCancLoading } =
    useGetBookingCancellationPolicyQuery();
  const { data: noShowPolicy, isLoading: isNoShowLoading } =
    useGetBookingNoShowPolicyQuery();

  const [updateCancellation, { isLoading: isUpdatingCancellation }] =
    useUpdateBookingCancellationPolicyMutation();
  const [updateNoShow, { isLoading: isUpdatingNoShow }] =
    useUpdateBookingNoShowPolicyMutation();

  useEffect(() => {
    if (activeTab === 'cancellation' && cancellationPolicy) {
      setFormData({
        free_cancellation_hours:
          typeof cancellationPolicy.free_cancellation_hours === 'number'
            ? cancellationPolicy.free_cancellation_hours
            : 0,
        penalty_type: cancellationPolicy.penalty_type as PenaltyType,
        penalty_value: cancellationPolicy.penalty_value,
        min_penalty_amount_eur:
          typeof cancellationPolicy.min_penalty_amount_eur === 'number'
            ? cancellationPolicy.min_penalty_amount_eur
            : '',
        max_penalty_amount_eur:
          typeof cancellationPolicy.max_penalty_amount_eur === 'number'
            ? cancellationPolicy.max_penalty_amount_eur
            : '',
      });
      setErrors({});
    }
    if (activeTab === 'no_show' && noShowPolicy) {
      setFormData({
        free_cancellation_hours: 0,
        penalty_type: noShowPolicy.penalty_type as PenaltyType,
        penalty_value: noShowPolicy.penalty_value,
        min_penalty_amount_eur:
          typeof noShowPolicy.min_penalty_amount_eur === 'number'
            ? noShowPolicy.min_penalty_amount_eur
            : '',
        max_penalty_amount_eur:
          typeof noShowPolicy.max_penalty_amount_eur === 'number'
            ? noShowPolicy.max_penalty_amount_eur
            : '',
      });
      setErrors({});
    }
  }, [activeTab, cancellationPolicy, noShowPolicy]);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        free_cancellation_hours:
          activeTab === 'no_show'
            ? Yup.number()
              .transform((v, o) => (o === '' ? undefined : v))
              .typeError('Must be a number')
              .min(0, 'Must be >= 0')
            : Yup.number()
              .transform((v, o) => (o === '' ? undefined : v))
              .typeError('Must be a number')
              .min(0, 'Must be >= 0')
              .required('Free cancellation window is required'),
        penalty_type: Yup.mixed<PenaltyType>()
          .oneOf(['FIXED', 'PERCENTAGE'])
          .required(),
        penalty_value: Yup.number()
          .transform((v, o) => (o === '' ? undefined : v))
          .typeError('Penalty value must be a number')
          .when('penalty_type', (pt: unknown, s: Yup.NumberSchema) => {
            if (pt === 'PERCENTAGE') {
              return s
                .min(0, 'Min 0')
                .max(100, 'Max 100')
                .test('max-2-decimals', 'Max 2 decimal places', (val) =>
                  val == null
                    ? false
                    : /^\d{1,3}(\.\d{1,2})?$/.test(String(val))
                )
                .required('Penalty value is required');
            }
            return s
              .min(0, 'Must be >= 0')
              .required('Penalty value is required');
          }),
        min_penalty_amount_eur: Yup.number()
          .transform((v, o) => (o === '' ? undefined : v))
          .typeError('Min amount must be a number')
          .min(0, 'Min amount must be >= 0')
          .when('penalty_type', (pt: unknown, s: Yup.NumberSchema) =>
            pt === 'PERCENTAGE'
              ? s.required('Min amount is required')
              : s.notRequired()
          ),
        max_penalty_amount_eur: Yup.number()
          .transform((v, o) => (o === '' ? undefined : v))
          .typeError('Max amount must be a number')
          .min(0, 'Max amount must be >= 0')
          .when('penalty_type', (pt: unknown, s: Yup.NumberSchema) =>
            pt === 'PERCENTAGE'
              ? s
                .required('Max amount is required')
                .min(
                  Yup.ref('min_penalty_amount_eur') as unknown as number,
                  'Max must be >= Min'
                )
              : s.notRequired()
          ),
      }),
    [activeTab]
  );

  const handleChange = (field: keyof FormState, value: unknown) => {
    let nextValue = value;
    if (field === 'penalty_value') {
      if (formData.penalty_type === 'PERCENTAGE') {
        if (value === '') {
          nextValue = '';
        } else {
          const num = typeof value === 'number' ? value : Number(value);
          const clamped = Math.min(Math.max(num, 0), 100);
          const rounded = Math.round(clamped * 100) / 100; // max 2 decimals
          nextValue = rounded;
        }
      } else if (value !== '') {
        const num = typeof value === 'number' ? value : Number(value);
        nextValue = Math.max(num, 0);
      }
    }
    setFormData((prev) => ({ ...prev, [field]: nextValue }) as FormState);
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const isBusy =
    isCancLoading ||
    isNoShowLoading ||
    isUpdatingCancellation ||
    isUpdatingNoShow;

  const handleSave = async () => {
    try {
      // Runtime guard to prevent saving invalid percentage values
      if (formData.penalty_type === 'PERCENTAGE') {
        const percentVal = Number((formData.penalty_value as number) ?? 0);
        if (percentVal > 100) {
          setErrors((prev) => ({
            ...prev,
            penalty_value: 'Max 100 for percentage',
          }));
          enqueueSnackbar('Percentage penalty cannot exceed 100%', {
            variant: 'error',
          });
          return;
        }

        if (percentVal < 0) {
          setErrors((prev) => ({
            ...prev,
            penalty_value: 'Min 0 for percentage',
          }));
          enqueueSnackbar('Percentage penalty cannot be negative', {
            variant: 'error',
          });
          return;
        }
        // Enforce at most 2 decimal places
        if (!Number.isInteger(percentVal * 100)) {
          setErrors((prev) => ({
            ...prev,
            penalty_value: 'Max 2 decimal places',
          }));
          enqueueSnackbar('Max 2 decimal places are allowed for percentage', {
            variant: 'error',
          });
          return;
        }
      }
      await schema.validate(formData, { abortEarly: false });
      if (activeTab === 'no_show') {
        const payload = {
          penalty_type: formData.penalty_type as PenaltyType,
          penalty_value:
            formData.penalty_type === 'PERCENTAGE'
              ? Math.round(
                Number((formData.penalty_value as number) ?? 0) * 100
              ) / 100
              : Number((formData.penalty_value as number) ?? 0),
          ...(formData.penalty_type === 'PERCENTAGE'
            ? {
              min_penalty_amount_eur: Number(formData.min_penalty_amount_eur),
              max_penalty_amount_eur: Number(formData.max_penalty_amount_eur),
            }
            : {}),
        };
        await updateNoShow(payload).unwrap();
        enqueueSnackbar('No show policy saved successfully', {
          variant: 'success',
        });
      } else {
        const payload = {
          free_cancellation_hours: Number(formData.free_cancellation_hours),
          penalty_type: formData.penalty_type as PenaltyType,
          penalty_value:
            formData.penalty_type === 'PERCENTAGE'
              ? Math.round(
                Number((formData.penalty_value as number) ?? 0) * 100
              ) / 100
              : Number((formData.penalty_value as number) ?? 0),
          ...(formData.penalty_type === 'PERCENTAGE'
            ? {
              min_penalty_amount_eur: Number(formData.min_penalty_amount_eur),
              max_penalty_amount_eur: Number(formData.max_penalty_amount_eur),
            }
            : {}),
        };
        await updateCancellation(payload).unwrap();
        enqueueSnackbar('Cancellation policy saved successfully', {
          variant: 'success',
        });
      }
    } catch (e: unknown) {
      if (e instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = {};
        e.inner.forEach((err) => {
          if (err.path) newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
        return;
      }
      const apiMessage = (e as { data?: { message?: string } })?.data?.message;
      enqueueSnackbar(apiMessage || 'Failed to save policy', {
        variant: 'error',
      });
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="penalty-config-management-heading" />
              <ToolbarDescription>
                Configure cancellation and no-show penalty policies.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={isBusy}
              >
                {isBusy ? 'Saving...' : 'Save changes'}
              </button>
            </ToolbarActions>
          </Toolbar>
          <div className="flex flex-col gap-4">
            <Tabs
              value={activeTab}
              onChange={(_, value) =>
                setActiveTab(
                  (value as 'cancellation' | 'no_show') || 'cancellation'
                )
              }
              className="w-full"
            >
              <div className="flex flex-col gap-4">
                <TabsList className="flex gap-4">
                  <Tab value="cancellation" className="text-sm">
                    Cancellation Penalty
                  </Tab>
                  <Tab value="no_show" className="text-sm">
                    No Show Config
                  </Tab>
                </TabsList>
                <TabPanel value="cancellation">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="free_cancellation_hours"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Free cancellation until
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <NumberInput
                          id="free_cancellation_hours"
                          className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                          min={0}
                          value={formData.free_cancellation_hours}
                          onChange={(e) =>
                            handleChange(
                              'free_cancellation_hours',
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value)
                            )
                          }
                          disabled={isBusy}
                          autoComplete="off"
                          aria-invalid={!!errors.free_cancellation_hours}
                          aria-describedby={
                            errors.free_cancellation_hours
                              ? 'free_cancellation_hours-error'
                              : undefined
                          }
                        />
                        <span className="text-sm text-gray-600">
                          hours before booking start time
                        </span>
                      </div>
                      {errors.free_cancellation_hours && (
                        <p
                          id="free_cancellation_hours-error"
                          className="text-red-500 text-xs mt-1"
                          role="alert"
                        >
                          {errors.free_cancellation_hours}
                        </p>
                      )}
                    </div>

                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 mb-2">
                        Penalty Type
                      </legend>
                      <div className="flex items-center gap-6">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="penalty_type"
                            className="form-radio"
                            autoComplete="off"
                            checked={formData.penalty_type === 'FIXED'}
                            onChange={() =>
                              handleChange('penalty_type', 'FIXED')
                            }
                            disabled={isBusy}
                          />
                          <span>Fixed amount</span>
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="penalty_type"
                            className="form-radio"
                            checked={formData.penalty_type === 'PERCENTAGE'}
                            onChange={() =>
                              handleChange('penalty_type', 'PERCENTAGE')
                            }
                            disabled={isBusy}
                          />
                          <span>Percentage of booking amount</span>
                        </label>
                      </div>
                    </fieldset>

                    <div>
                      <label
                        htmlFor="penalty_value"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Penalty Value
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <NumberInput
                          id="penalty_value"
                          className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                          min={0}
                          autoComplete="off"
                          step={
                            formData.penalty_type === 'PERCENTAGE' ? 0.01 : 0.01
                          }
                          value={formData.penalty_value}
                          onChange={(e) =>
                            handleChange(
                              'penalty_value',
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value)
                            )
                          }
                          disabled={isBusy}
                          aria-invalid={!!errors.penalty_value}
                          aria-describedby={
                            errors.penalty_value
                              ? 'penalty_value-error'
                              : undefined
                          }
                        />
                        {formData.penalty_type === 'FIXED' ? (
                          <span className="text-sm text-gray-600">EUR</span>
                        ) : (
                          <span className="text-sm text-gray-600">%</span>
                        )}
                      </div>
                      {formData.penalty_type === 'PERCENTAGE' && (
                        <div className="text-xs text-gray-500 mt-1">
                          Allowed range 0–100. Max 2 decimals.
                        </div>
                      )}
                      {errors.penalty_value && (
                        <p
                          id="penalty_value-error"
                          className="text-red-500 text-xs mt-1"
                          role="alert"
                        >
                          {errors.penalty_value}
                        </p>
                      )}
                    </div>

                    {formData.penalty_type === 'PERCENTAGE' && (
                      <>
                        <div>
                          <label
                            htmlFor="min_penalty_amount_eur"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Min penalty amount (EUR)
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <NumberInput
                            id="min_penalty_amount_eur"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            min={0}
                            step={0.01}
                            value={
                              formData.min_penalty_amount_eur as number | ''
                            }
                            onChange={(e) =>
                              handleChange(
                                'min_penalty_amount_eur',
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value)
                              )
                            }
                            disabled={isBusy}
                            aria-invalid={!!errors.min_penalty_amount_eur}
                            aria-describedby={
                              errors.min_penalty_amount_eur
                                ? 'min_penalty_amount_eur-error'
                                : undefined
                            }
                            autoComplete="off"
                          />
                          {errors.min_penalty_amount_eur && (
                            <p
                              id="min_penalty_amount_eur-error"
                              className="text-red-500 text-xs mt-1"
                              role="alert"
                            >
                              {errors.min_penalty_amount_eur}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="max_penalty_amount_eur"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Max penalty amount (EUR)
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <NumberInput
                            id="max_penalty_amount_eur"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            min={0}
                            step={0.01}
                            autoComplete="off"
                            value={
                              formData.max_penalty_amount_eur as number | ''
                            }
                            onChange={(e) =>
                              handleChange(
                                'max_penalty_amount_eur',
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value)
                              )
                            }
                            disabled={isBusy}
                            aria-invalid={!!errors.max_penalty_amount_eur}
                            aria-describedby={
                              errors.max_penalty_amount_eur
                                ? 'max_penalty_amount_eur-error'
                                : undefined
                            }
                          />
                          {errors.max_penalty_amount_eur && (
                            <p
                              id="max_penalty_amount_eur-error"
                              className="text-red-500 text-xs mt-1"
                              role="alert"
                            >
                              {errors.max_penalty_amount_eur}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <div className="md:col-span-2 text-xs text-gray-600">
                      <div>
                        Penalty uses the same VAT rate and tax regime as the
                        original booking.
                      </div>
                      <div>
                        Penalty amount is recorded as a separate financial line
                        item.
                      </div>
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value="no_show">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 mb-2">
                        Penalty Type
                      </legend>
                      <div className="flex items-center gap-6">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="penalty_type"
                            className="form-radio"
                            autoComplete="off"
                            checked={formData.penalty_type === 'FIXED'}
                            onChange={() =>
                              handleChange('penalty_type', 'FIXED')
                            }
                            disabled={isBusy}
                          />
                          <span>Fixed amount</span>
                        </label>
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="radio"
                            name="penalty_type"
                            className="form-radio"
                            checked={formData.penalty_type === 'PERCENTAGE'}
                            onChange={() =>
                              handleChange('penalty_type', 'PERCENTAGE')
                            }
                            disabled={isBusy}
                          />
                          <span>Percentage of booking amount</span>
                        </label>
                      </div>
                    </fieldset>

                    <div>
                      <label
                        htmlFor="penalty_value_no_show"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Penalty Value
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <NumberInput
                          id="penalty_value_no_show"
                          autoComplete="off"
                          className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                          min={0}
                          step={
                            formData.penalty_type === 'PERCENTAGE' ? 0.01 : 0.01
                          }
                          value={formData.penalty_value}
                          onChange={(e) =>
                            handleChange(
                              'penalty_value',
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value)
                            )
                          }
                          disabled={isBusy}
                          aria-invalid={!!errors.penalty_value}
                          aria-describedby={
                            errors.penalty_value
                              ? 'penalty_value-error'
                              : undefined
                          }
                        />
                        {formData.penalty_type === 'FIXED' ? (
                          <span className="text-sm text-gray-600">EUR</span>
                        ) : (
                          <span className="text-sm text-gray-600">%</span>
                        )}
                      </div>
                      {formData.penalty_type === 'PERCENTAGE' && (
                        <div className="text-xs text-gray-500 mt-1">
                          Allowed range 0–100. Max 2 decimals.
                        </div>
                      )}
                      {errors.penalty_value && (
                        <p
                          id="penalty_value-error"
                          className="text-red-500 text-xs mt-1"
                          role="alert"
                        >
                          {errors.penalty_value}
                        </p>
                      )}
                    </div>

                    {formData.penalty_type === 'PERCENTAGE' && (
                      <>
                        <div>
                          <label
                            htmlFor="min_penalty_amount_eur_no_show"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Min penalty amount (EUR)
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <NumberInput
                            id="min_penalty_amount_eur_no_show"
                            autoComplete="off"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            min={0}
                            step={0.01}
                            value={
                              formData.min_penalty_amount_eur as number | ''
                            }
                            onChange={(e) =>
                              handleChange(
                                'min_penalty_amount_eur',
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value)
                              )
                            }
                            disabled={isBusy}
                            aria-invalid={!!errors.min_penalty_amount_eur}
                            aria-describedby={
                              errors.min_penalty_amount_eur
                                ? 'min_penalty_amount_eur-error'
                                : undefined
                            }
                          />
                          {errors.min_penalty_amount_eur && (
                            <p
                              id="min_penalty_amount_eur-error"
                              className="text-red-500 text-xs mt-1"
                              role="alert"
                            >
                              {errors.min_penalty_amount_eur}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="max_penalty_amount_eur_no_show"
                            className="block text-sm font-medium text-gray-700 mb-2"
                          >
                            Max penalty amount (EUR)
                            <span className="text-red-500 ml-1">*</span>
                          </label>
                          <NumberInput
                            id="max_penalty_amount_eur_no_show"
                            className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                            min={0}
                            autoComplete="off"
                            step={0.01}
                            value={
                              formData.max_penalty_amount_eur as number | ''
                            }
                            onChange={(e) =>
                              handleChange(
                                'max_penalty_amount_eur',
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value)
                              )
                            }
                            disabled={isBusy}
                            aria-invalid={!!errors.max_penalty_amount_eur}
                            aria-describedby={
                              errors.max_penalty_amount_eur
                                ? 'max_penalty_amount_eur-error'
                                : undefined
                            }
                          />
                          {errors.max_penalty_amount_eur && (
                            <p
                              id="max_penalty_amount_eur-error"
                              className="text-red-500 text-xs mt-1"
                              role="alert"
                            >
                              {errors.max_penalty_amount_eur}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <div className="md:col-span-2 text-xs text-gray-600">
                      <div>
                        Penalty uses the same VAT rate and tax regime as the
                        original booking.
                      </div>
                      <div>
                        Penalty amount is recorded as a separate financial line
                        item.
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </div>
            </Tabs>
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export { PenaltyConfigManagement };
